import FileService, { CHUNK_SIZE } from '@/modules/file/fileService'
import {
  addFiles,
  setPending,
  closeUploadDrawer,
  toggleUploadsOverlay,
  removeFromChecksumQueue,
  addToChecksumQueue,
  clearUploadInterval,
  removeFromUploadQueue,
  addTodoUpload,
  updateTodoUploadProgress,
  removeTodoUpload,
} from '@/redux/ducks/document/upload'
import { store, dispatch, getAppState } from '@/redux/store'
import { user } from 'ampersand-app'
import { experimentalFeatures } from '@/modules/base/util/constants'
import { workerMessages } from '@/worker/constants'
import { v4 as uuidv4 } from 'uuid'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'
// FLAG:useDocuAi — pure per-file terminal-state classifier, kept in a leaf module so it stays
// unit-testable without pulling in the store/FileService dependency graph. Re-exported for
// existing importers.
import { classifyBatchUploads } from '@/redux/ducks/document/_helpers/classifyBatchUploads'
// FLAG:useDocuAi — an OPEN duplicate prompt is the user thinking, not a wedged batch (F15.2).
import { hasOpenDuplicateUploadPrompt } from '@/modules/file/_helpers/duplicateUploadPrompt'
// FLAG:useDocuAi — admission rule for the upload queue (see the plan's "Scheduler design").
import { findAdmissibleUploadIndex, isReceiptUploadSource } from '@/redux/ducks/document/_helpers/uploadAdmission'

export { classifyBatchUploads }

const SIMULTANEOUS_UPLOADS_COUNT = 5
const SIMULTANEOUS_CHECKSUM_COUNT = 5

// FLAG:useDocuAi — receipt-batch stall detection. A batch is only considered wedged when NO file
// reached a terminal state for this long; a batch that keeps completing files runs as long as it
// needs to. The legacy path keeps its fixed 5-minute deadline (see runLegacyUploadBatch).
const STALL_TIMEOUT_MS = 180000
const STALL_CHECK_INTERVAL_MS = 15000

// FLAG:useDocuAi — uuids of receipt uploads that currently occupy a concurrency slot. A uuid is
// added the moment its prepare-upload is DISPATCHED (not when the pending row appears) and removed
// only when the file is terminal — which, because startSingleUpload now defers `finished` until
// complete-upload resolves, means the slot spans prepare → upload → complete.
//
// Module-level rather than redux on purpose: it changes many times per second and every write
// would re-run the app-wide store.subscribe in runReceiptUploadBatch.
//
// Legacy sources never enter this set; their admission still runs off the pending-row count.
const inFlightReceiptUploads = new Set<string>()

const occupyReceiptUploadSlot = (uuid: string) => {
  inFlightReceiptUploads.add(uuid)
}

export const releaseReceiptUploadSlot = (uuid: string) => {
  if (!inFlightReceiptUploads.delete(uuid)) return
  // Hand the freed slot to the next queued file immediately instead of waiting up to 250ms for
  // the next tick. Deferred to a macrotask: this runs inside a store.subscribe callback and
  // processUploads dispatches.
  setTimeout(processUploads, 0)
}

// Backstop against a leaked slot permanently shrinking the pool for later batches (e.g. a file
// whose pending row was removed by a cancel, so it never reaches a terminal classification).
export const releaseReceiptUploadSlots = (uuids: string[]) => {
  uuids.forEach((uuid) => inFlightReceiptUploads.delete(uuid))
  setTimeout(processUploads, 0)
}

export const fetchPendingUploads = () => {
  if (!user?.experimentalFeatures?.includes(experimentalFeatures.dmsDirectUpload)) {
    return
  }
  return FileService.fetchPendingUploads().then(({ data }) => {
    dispatch(setPending(data))
  })
}

export const processUploads = () => {
  const worker = getAppState().worker.worker
  const calculations = [...getAppState().document.upload.checksumQueue]
  const queuedUploads = [...getAppState().document.upload.uploadQueue]
  const pending = getAppState().document.upload.pending

  if (!calculations.length && !queuedUploads.length) {
    dispatch(clearUploadInterval())
    return
  }

  if (calculations.length && calculations.filter(({ running }) => !!running).length < SIMULTANEOUS_CHECKSUM_COUNT) {
    const payload = calculations.shift()
    worker.postMessage({ message: workerMessages.FILE_CALCULATE_UPLOAD, payload })
    dispatch(removeFromChecksumQueue(payload.uuid))
  }

  // FLAG:useDocuAi — admission is now decided per item by its source. For a queue of legacy items
  // only (every flag-OFF case) findAdmissibleUploadIndex returns 0 exactly when the previous
  // `queuedUploads.length && pendingUploadCount < SIMULTANEOUS_UPLOADS_COUNT` test passed and -1
  // exactly when it did not, and shift() took that same index-0 item — identical behaviour.
  // Receipt items are instead gated on the in-flight set, which counts a file from the moment its
  // prepare is dispatched, closing the window that let the 250ms tick over-admit (RC1).
  const admissibleIndex = findAdmissibleUploadIndex({
    queue: queuedUploads,
    pending,
    inFlightReceiptCount: inFlightReceiptUploads.size,
    limit: SIMULTANEOUS_UPLOADS_COUNT,
  })

  if (admissibleIndex !== -1) {
    const payload = queuedUploads[admissibleIndex]
    const { file, uuid, name, size, checksum, params, scan, totalChunks, source } = payload
    dispatch(removeFromUploadQueue(uuid))
    if (isReceiptUploadSource(source)) {
      occupyReceiptUploadSlot(uuid)
    }
    FileService.prepareUpload({ file, uuid, name, size, checksum, source }, params, scan, totalChunks)
  }
}

const enqueueUploadCalculations = ({ uploads, params, scan }) => {
  const calculations = []
  uploads.forEach((upload) => {
    const totalChunks = Math.ceil(upload.size / CHUNK_SIZE)
    calculations.push({
      ...upload,
      fileName: upload.name,
      params,
      scan,
      totalChunks,
      currentChunk: 0,
    })
  })
  dispatch(addToChecksumQueue(calculations))
  setTimeout(() => {
    dispatch(toggleUploadsOverlay(true))
  }, 800)
}

// FLAG:useDocuAi — new terminal-state accounting for the receipt uploaders (source 'expense' /
// 'revenue'). Uses the pure classifyBatchUploads classifier plus a monotonic `accounted` set so the
// batch settles as soon as every file reaches a terminal state (fixes the endless-spinner hang) and
// surfaces per-file failure reasons in the flyout. Only reached with useDocuAi on (call sites gate on
// isDmsReceiptUploadEnabled).
const runReceiptUploadBatch = ({ uploads, params, scan, closeOverlayAfterComplete, todoId, onFileUploaded, onProgress, resolve, reject }) => {
  const completedFiles = new Map() // uuid -> { id, name } for successful non-todo uploads
  const completedTodoFiles = new Map() // uuid -> { id, name } for successful todo uploads
  const failedFiles = new Map() // uuid -> { name, error } for terminally failed uploads
  const errors = []
  // Uploads whose one-time side effects (todo linking, todo progress) have already fired. The
  // success/failure classification itself is recomputed from the store on every tick, but each
  // file is only acted on — and only counted — once.
  const accounted = new Set()
  // Receipt uploads resolve through the non-todo branch below. The todo-only resolve shape must
  // only apply when todo files exist, otherwise a non-todo upload (0 todo files) would resolve empty.
  const totalTodoUploads = uploads.filter((upload) => upload.source === 'todo').length

  // Stall detection (replaces the fixed 5-minute deadline). A batch that keeps producing terminal
  // files is making progress and must never be timed out — 115 files legitimately take longer than
  // any fixed wall-clock budget. Only a batch that produces NOTHING for STALL_TIMEOUT_MS is wedged.
  let lastProgressAt = Date.now()
  let stallTimer = null
  // F15.2 — NON-terminal activity also counts as progress: a single large file on a slow line
  // legitimately needs longer than STALL_TIMEOUT_MS to reach its first terminal, but its pending
  // row keeps changing (attempt/completing/running/progress). A fingerprint of the batch rows
  // catches that; only a batch whose rows are FROZEN is wedged.
  const batchUuids = new Set(uploads.map((upload) => upload.uuid))
  let lastActivitySignature = ''
  const readActivitySignature = (uploadState) => {
    const rows = (uploadState.pending || [])
      .filter((row) => batchUuids.has(row.uuid))
      .map((row) => `${row.uuid}:${row.attempt ?? ''}:${row.progress ?? ''}:${row.completing ? 1 : 0}:${row.running ? 1 : 0}:${row.finished ? 1 : 0}:${row.error ? 1 : 0}`)
    const calculating = (uploadState.calculating || [])
      .filter((entry) => batchUuids.has(entry.uuid)).length
    return `${rows.join('|')}#${calculating}`
  }

  const stopStallTimer = () => {
    if (stallTimer) {
      clearInterval(stallTimer)
      stallTimer = null
    }
  }

  // Free every slot this batch still holds. Belt-and-braces: individual slots are released as
  // each file turns terminal, but a file whose pending row was removed before it was classified
  // (e.g. the user cancelled it) would otherwise leak its slot into the next batch.
  const releaseAllSlots = () => releaseReceiptUploadSlots(uploads.map((upload) => upload.uuid))

  const unsubscribe = store.subscribe(() => {
    const uploadState = getAppState().document.upload
    // F15.2 — refresh the stall clock on ANY observable change of this batch's rows, not just
    // terminal transitions.
    const activitySignature = readActivitySignature(uploadState)
    if (activitySignature !== lastActivitySignature) {
      lastActivitySignature = activitySignature
      lastProgressAt = Date.now()
    }
    const results = classifyBatchUploads({
      uploads,
      pending: uploadState.pending,
      calculating: uploadState.calculating,
    })

    results.forEach((result) => {
      if (result.status === 'pending' || accounted.has(result.uuid)) {
        return
      }
      accounted.add(result.uuid)
      // The file is terminal (complete-upload included — startSingleUpload no longer marks a row
      // finished before completing), so its concurrency slot is genuinely free now.
      releaseReceiptUploadSlot(result.uuid)
      lastProgressAt = Date.now()

      if (result.status === 'success') {
        if (result.source === 'todo') {
          completedTodoFiles.set(result.uuid, { id: result.fileId, name: result.name })
          onFileUploaded?.(result.fileId)
        } else {
          // Any non-todo source (documents, expense, revenue) returns its created file id here.
          completedFiles.set(result.uuid, { id: result.fileId, name: result.name })
        }
      } else {
        // Terminal failure — recorded so the promise settles immediately instead of hanging
        // until the 5-minute timeout (the endless-spinner bug). The per-file reason is also
        // surfaced in the upload-status flyout by pending-upload/calculating-upload.
        failedFiles.set(result.uuid, { name: result.name, error: result.error })
        errors.push(result.error)
      }

      // Keep the per-todo progress badge in sync with terminal (done or failed) todo files.
      if (result.source === 'todo' && todoId) {
        const finishedTodoCount = results.filter((item) => item.source === 'todo' && item.status !== 'pending').length
        dispatch(updateTodoUploadProgress({
          todoId,
          finishedCount: finishedTodoCount,
          hasErrors: failedFiles.size > 0,
        }))
      }

      // Per-file progress for the dropzone tile. Without this the receipt tile sat at
      // "0 von N hochgeladen" for the entire batch — it was only ever set once at the start and
      // once at the end, so a long batch was indistinguishable from a hung one.
      onProgress?.({ done: accounted.size, total: uploads.length, failed: failedFiles.size })
    })

    // Wait until every file in the batch has reached a terminal state at least once. Using the
    // monotonic `accounted` set (rather than re-reading the store) keeps this robust even if a
    // finished/errored pending row is later removed — e.g. the user closing the upload overlay,
    // which filters finished rows out of state.
    if (accounted.size < uploads.length) {
      return
    }

    unsubscribe()
    stopStallTimer()
    releaseAllSlots()

    const completedWithErrors = failedFiles.size
    const uniqueCompletedFiles = Array.from(completedFiles.values())
    const uniqueCompletedTodoFiles = Array.from(completedTodoFiles.values())

    if (todoId) {
      dispatch(removeTodoUpload(todoId))
    }

    // Todo uploads resolve with just the (successfully linked) todo files — the shape the todo
    // forms consume. Overlay closes regardless of errors, matching the prior contract.
    if (totalTodoUploads > 0) {
      if (closeOverlayAfterComplete) {
        dispatch(toggleUploadsOverlay(false))
      }
      resolve({ data: uniqueCompletedTodoFiles })
      return
    }

    if (closeOverlayAfterComplete && completedWithErrors === 0) {
      dispatch(toggleUploadsOverlay(false))
    }

    resolve({
      data: [...uniqueCompletedFiles, ...uniqueCompletedTodoFiles],
      completedWithErrors,
      errors,
      // F15.3 — identity-true retry set: the caller's name join misfires on same-named files
      // (Scanner-Output 'scan.pdf' twice), so hand back the failed entries WITH their uuid and
      // the original File reference.
      failedUploads: uploads
        .filter((upload) => failedFiles.has(upload.uuid))
        .map((upload) => ({ uuid: upload.uuid, name: upload.name, file: upload.file })),
    })
  })

  enqueueUploadCalculations({ uploads, params, scan })

  // Backstop only — the subscriber above settles as soon as every file reaches a terminal state.
  // This fires when the batch has been completely silent for STALL_TIMEOUT_MS, which is the only
  // honest definition of "wedged": the previous fixed 5-minute deadline rejected healthy large
  // batches (115 files cannot finish in 5 minutes) while still waiting a pointless 5 minutes for a
  // batch that died in the first second.
  stallTimer = setInterval(() => {
    // F15.2 — an open "Dokument bereits vorhanden" prompt is the user thinking; the batch
    // deliberately waits for the answer (duplicateUploadPrompt serialises the modals and holds
    // the slots). Never time that out.
    if (hasOpenDuplicateUploadPrompt()) {
      lastProgressAt = Date.now()
      return
    }
    if (Date.now() - lastProgressAt < STALL_TIMEOUT_MS) return

    stopStallTimer()
    unsubscribe()
    releaseAllSlots()

    const settledCount = completedFiles.size + completedTodoFiles.size + failedFiles.size
    if (settledCount !== uploads.length) {
      if (todoId) {
        dispatch(removeTodoUpload(todoId))
      }
      const stuckUploads = uploads.filter((upload) => !accounted.has(upload.uuid))
      const stuck = stuckUploads.map((upload) => upload.name)
      const error = new Error(`Upload stalled: ${stuck.length} file(s) made no progress for ${Math.round(STALL_TIMEOUT_MS / 1000)}s`)
      // Name the files so the caller's failure tile can report them instead of a bare timeout.
      ;(error as Error & { stalledFiles?: string[] }).stalledFiles = stuck
      // F15.2 — the sub-batch's already-settled results must survive the reject: without them the
      // caller counted the WHOLE batch as failed, lost the successes from its aggregate and the
      // tile retry re-uploaded files that had long since landed.
      ;(error as Error & { partial?: unknown }).partial = {
        data: [...Array.from(completedFiles.values()), ...Array.from(completedTodoFiles.values())],
        completedWithErrors: failedFiles.size,
        errors: errors.slice(),
        failedUploads: uploads
          .filter((upload) => failedFiles.has(upload.uuid))
          .map((upload) => ({ uuid: upload.uuid, name: upload.name, file: upload.file })),
      }
      // F15.3 — the stalled files themselves, with their File references, for an identity-true retry.
      ;(error as Error & { stalledUploads?: unknown }).stalledUploads = stuckUploads
        .map((upload) => ({ uuid: upload.uuid, name: upload.name, file: upload.file }))
      reject(error)
    }
  }, STALL_CHECK_INTERVAL_MS)
}

// FLAG:useDocuAi — baseline upload accounting for every LEGACY source (documents, todo). Kept
// byte-for-byte identical to f2ac6a682f so flag-OFF (and flag-ON todo/document uploads) behave
// exactly as before the receipt-review feature — the ungated rewrite had changed the Todo error
// toast, the retry-after-failure linking, and the document-upload-view rejection semantics.
const runLegacyUploadBatch = ({ uploads, params, scan, closeOverlayAfterComplete, todoId, onFileUploaded, resolve, reject }) => {
  let completedCount = 0
  let completedTodoFileCount = 0
  let completedWithErrors = 0
  const completedFiles = new Map() // Use Map to track unique files by UUID
  const completedTodoFiles = new Map()
  const errors = []

  const unsubscribe = store.subscribe(() => {
    const state = getAppState()
    const pending = state.document.upload.pending

    pending.forEach((upload) => {
      const originalUpload = uploads.find((u) => u.name === upload.fileName)
      if (!originalUpload) return

      if (upload.finished) {
        if (upload.error) {
          errors.push(upload.error)
          completedWithErrors++
        }

        if (originalUpload?.source === 'todo' && !completedTodoFiles.has(originalUpload.uuid)) {
          completedTodoFiles.set(originalUpload.uuid, {
            id: upload.fileId,
            name: upload.fileName,
          })
          onFileUploaded?.(upload?.fileId)
          completedTodoFileCount++

          // Update todo upload progress
          if (todoId) {
            dispatch(updateTodoUploadProgress({
              todoId,
              finishedCount: completedTodoFileCount,
              hasErrors: completedWithErrors > 0,
            }))
          }
        } else if (originalUpload?.source === 'documents' && !completedFiles.has(originalUpload.uuid)) {
          completedFiles.set(originalUpload.uuid, {
            id: upload.fileId,
            name: upload.fileName,
          })
          completedCount++
        }

        if (completedTodoFileCount === uploads.filter((upload) => upload.source === 'todo').length) {
          if (closeOverlayAfterComplete) {
            dispatch(toggleUploadsOverlay(false))
          }
          const uniqueCompletedTodoFiles = Array.from(completedTodoFiles.values())

          // Remove todo upload tracking when complete
          if (todoId) {
            dispatch(removeTodoUpload(todoId))
          }

          unsubscribe()
          resolve({ data: uniqueCompletedTodoFiles })
        }

        if (completedCount + completedWithErrors === uploads.length || completedTodoFileCount + completedWithErrors === uploads.filter((upload) => upload.source === 'todo').length) {
          unsubscribe()

          const uniqueCompletedFiles = Array.from(completedFiles.values())
          const uniqueCompletedTodoFiles = Array.from(completedTodoFiles.values())
          if (closeOverlayAfterComplete && completedWithErrors === 0) {
            dispatch(toggleUploadsOverlay(false))
          }

          // Remove todo upload tracking when complete
          if (todoId) {
            dispatch(removeTodoUpload(todoId))
          }

          resolve({ data: [...uniqueCompletedFiles, ...uniqueCompletedTodoFiles], completedWithErrors, errors })
        }
      }
    })
  })

  enqueueUploadCalculations({ uploads, params, scan })

  setTimeout(() => {
    unsubscribe()
    if (completedCount + completedWithErrors !== uploads.length) {
      // Remove todo upload tracking on timeout
      if (todoId) {
        dispatch(removeTodoUpload(todoId))
      }
      reject(new Error('Upload timeout'))
    }
  }, 300000) // 5 minute timeout
}

export const executeUpload = async({ params, scan, files, closeOverlayAfterComplete = false, source = 'documents', todoId, onFileUploaded, onProgress }) => {
  if (files.length === 0) {
    return Promise.resolve({ data: [] })
  }

  // Track todo uploads if todoId is provided
  if (todoId && source === 'todo') {
    dispatch(addTodoUpload({ todoId, totalCount: files.length }))
  }

  return new Promise((resolve, reject) => {
    const uploads = files.map((file) => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      uuid: uuidv4(),
      source,
      todoId, // Add todoId to upload tracking
    }))
    dispatch(closeUploadDrawer())

    // FLAG:useDocuAi — receipt uploads (source 'expense'/'revenue') use the new classifyBatchUploads
    // accounting; every legacy source (documents, todo) keeps the f2ac6a682f baseline subscriber
    // VERBATIM. Receipt sources only reach executeUpload with useDocuAi on (call sites gate on
    // isDmsReceiptUploadEnabled), so flag-OFF stays baseline-identical by construction — this also
    // restores the legacy todo/document error+retry semantics that the ungated rewrite had changed.
    const runBatch = (source === 'expense' || source === 'revenue') ? runReceiptUploadBatch : runLegacyUploadBatch
    runBatch({ uploads, params, scan, closeOverlayAfterComplete, todoId, onFileUploaded, onProgress, resolve, reject })
  })
}

const createFileObject = (item, source = 'documents') => ({
  id: item.id,
  name: item.file.name,
  size: item.file.size,
  type: item.file.type,
  mimetype: item.file.type,
  source,
  isUpload: true,
  file: item.file,
})

export const isDmsUploadEnabled = () => {
  return user?.experimentalFeatures?.includes(experimentalFeatures.dmsDirectUpload)
}

// FLAG:useDocuAi — the expense/revenue uploaders route through the DMS→DocuAI pipeline only
// when BOTH the DMS direct-upload infra (dmsDirectUpload) AND the DocuAI analysis feature
// (useDocuAi) are enabled. If either flag is off, the receipt uploaders keep the legacy
// POST /api/file/upload path (byte-identical to today).
export const isDmsReceiptUploadEnabled = () => {
  const features = user?.experimentalFeatures
  return Boolean(
    features?.includes(experimentalFeatures.dmsDirectUpload)
    && features?.includes(experimentalFeatures.useDocuAi),
  )
}

export const createDmsUploader = (handleUpload, defaultUploader) => {
  return isDmsUploadEnabled()
    ? {
      methods: {
        addFiles: async(files) => {
          const batchItems = files.map(file => ({
            id: Math.random().toString(36).substring(7),
            file,
          }))
          await handleUpload(batchItems)
        },
      },
    }
    : defaultUploader
}

export const createDmsUploadHandler = (dispatch, onSuccess, source = 'documents') => {
  return async(files, e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (files.length > 25) {
      toast.showToast({ type: 'error', message: lang.fileUploaderTooManyItemsError(25) })
      return
    }

    await dispatch(addFiles(files))

    const fileObjects = files.map(file => createFileObject(file, source))
    onSuccess(fileObjects)
  }
}
